import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/SEO";

const empty = { title: "", title_en: "", organization: "", category: "govt", description: "", deadline: "", apply_url: "", location: "", salary: "", vacancy: 0 };

export default function AdminJobCirculars() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editId, setEditId] = useState(null);

    const load = () => api.get("/job-circulars").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, vacancy: Number(form.vacancy) };
            if (editId) await api.put(`/admin/job-circulars/${editId}`, payload);
            else await api.post("/admin/job-circulars", payload);
            toast.success("Saved"); setOpen(false); load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/admin/job-circulars/${id}`); load(); } };

    return (
        <div>
            <SEO title="Admin - Job Circulars" />
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-brand-navy">Job Circulars</h1>
                <button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="btn-primary px-4 py-2 rounded-lg inline-flex items-center gap-2 font-semibold"><Plus className="w-4 h-4" /> Add</button>
            </div>
            <div className="bg-white rounded-2xl border border-brand-light overflow-x-auto">
                <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-brand-light/40">
                        <tr><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Organization</th><th className="text-left px-4 py-3">Cat.</th><th className="text-left px-4 py-3">Deadline</th><th className="text-left px-4 py-3">Vacancy</th><th></th></tr>
                    </thead>
                    <tbody>
                        {items.map((j) => (
                            <tr key={j.id} className="border-t border-brand-light">
                                <td className="px-4 py-3 font-medium max-w-sm truncate">{j.title}</td>
                                <td className="px-4 py-3">{j.organization}</td>
                                <td className="px-4 py-3 text-xs">{j.category}</td>
                                <td className="px-4 py-3 text-xs">{j.deadline}</td>
                                <td className="px-4 py-3 font-semibold">{j.vacancy}</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => { setForm({ ...empty, ...j }); setEditId(j.id); setOpen(true); }} className="p-1.5 hover:bg-brand-light/50 rounded"><Edit className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => del(j.id)} className="p-1.5 hover:bg-brand-red/10 text-brand-red rounded ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl w-[95vw] sm:w-full font-bn max-h-[90vh] overflow-y-auto p-3 sm:p-6 z-[100]">
                    <DialogHeader><DialogTitle className="text-lg sm:text-xl">{editId ? "Edit" : "New"} Circular</DialogTitle></DialogHeader>
                    <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required className="sm:col-span-2" />
                        <Field label="Organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} required />
                        <div>
                            <label className="block text-xs mb-1 font-medium">Category</label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="govt">Govt</SelectItem><SelectItem value="bank">Bank</SelectItem><SelectItem value="private">Private</SelectItem><SelectItem value="ngo">NGO</SelectItem></SelectContent>
                            </Select>
                        </div>
                        <Field label="Deadline" value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} />
                        <Field label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
                        <Field label="Salary" value={form.salary} onChange={(v) => setForm({ ...form, salary: v })} />
                        <Field label="Vacancy" type="number" value={form.vacancy} onChange={(v) => setForm({ ...form, vacancy: v })} />
                        <Field label="Apply URL" value={form.apply_url} onChange={(v) => setForm({ ...form, apply_url: v })} className="sm:col-span-2" />
                        <div className="sm:col-span-2">
                            <label className="block text-xs mb-1 font-medium">Description</label>
                            <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-brand-light font-bn" />
                        </div>
                        <div className="sm:col-span-2 flex justify-end gap-2"><button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border border-brand-light">Cancel</button><button className="btn-primary px-4 py-2 rounded-lg font-semibold">Save</button></div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", required, className = "" }) {
    return (
        <div className={className}>
            <label className="block text-xs mb-1 font-medium">{label}{required && " *"}</label>
            <input type={type} value={value ?? ""} required={required} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-brand-light" />
        </div>
    );
}
